import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiGetRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";

// Fetches GET /api/community/dashboard and adapts it to the shape the
// container renders (COMY1-350).
//
// The API deliberately returns raw, unlocalized values — snake_case keys,
// `YYYY-MM` months, English taxonomy names — because Admin_system has no
// request-language convention and every other endpoint behaves the same way.
// All the localizing happens here, in one place, so the container stays
// presentational. Mirrors useSupplierDashboard (TBBR-61) on purpose.

/** `2026-07` → "Jul '26" / "יולי 26" (short) or "July 2026" (full). */
const formatMonth = (ym, lang, style) => {
  if (!ym) return "";
  const [year, month] = ym.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, 1));
  const opts =
    style === "full"
      ? { month: "long", year: "numeric", timeZone: "UTC" }
      : style === "nameOnly"
        ? { month: "short", timeZone: "UTC" }
        : { month: "short", year: "2-digit", timeZone: "UTC" };
  return new Intl.DateTimeFormat(lang, opts).format(date);
};

export const useCommunityDashboard = () => {
  const { t, i18n } = useTranslation("page-dashboard");
  // Taxonomy names are stored in English and translated client-side, with the
  // English name as the key. Service categories live in categories.json and
  // store categories in products_categories.json — the two "top" tables read
  // different trees, so each gets its own namespace. Supplier and community
  // names are user-entered and never translated.
  const { t: tCategories } = useTranslation("categories");
  const { t: tProductCategories } = useTranslation("products_categories");
  const { t: tRestaurantCategories } = useTranslation("restaurants_categories");

  const [raw, setRaw] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiGetRequest(endpoints.getCommunityDashboard);
      setRaw(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const lang = (i18n.language || "en").toLowerCase().startsWith("he")
    ? "he"
    : "en";

  const data = useMemo(() => {
    if (!raw) return null;

    const translateWith = (translate) => (name) =>
      name
        ? translate(name, {
            defaultValue: name,
            nsSeparator: false,
            keySeparator: false,
          })
        : name;
    const serviceCategoryName = translateWith(tCategories);
    const productCategoryName = translateWith(tProductCategories);
    const restaurantCategoryName = translateWith(tRestaurantCategories);

    const sparkPoints = (points) =>
      (points || []).map((p) => ({
        value: p.value ?? 0,
        label: formatMonth(p.month, lang, "short"),
        full: formatMonth(p.month, lang, "full"),
      }));

    // Quality trends carry null for "no orders that month" — keep the null so
    // the cell renders a dash instead of a fabricated 0%.
    const trendPairs = (points) =>
      (points || []).map((p) => [formatMonth(p.month, lang, "nameOnly"), p.value]);

    const quality = (block) => ({
      metrics: {
        fulfillment: {
          raw: block.metrics.fulfillment.raw,
          trend: trendPairs(block.metrics.fulfillment.trend),
        },
        approval: {
          raw: block.metrics.approval.raw,
          trend: trendPairs(block.metrics.approval.trend),
        },
        completion: {
          raw: block.metrics.completion.raw,
          trend: trendPairs(block.metrics.completion.trend),
        },
        cancellation: {
          raw: block.metrics.cancellation.raw,
          trend: trendPairs(block.metrics.cancellation.trend),
        },
      },
      suppliers: (block.suppliers || []).map((s) => ({
        name: s.name,
        image: s.image,
        fulfillment: s.fulfillment,
        approvalHours: s.approval_hours,
        cancellationRate: s.cancellation_rate,
      })),
    });

    return {
      currencySymbol: raw.currency_symbol,

      transactions: {
        countAllTime: raw.transactions.count_all_time,
        moneyAllTime: raw.transactions.money_all_time,
        countThisMonth: raw.transactions.count_this_month,
        moneyThisMonth: raw.transactions.money_this_month,
        monthly: sparkPoints(raw.transactions.monthly),
      },

      managerEarnings: {
        allTime: raw.manager_earnings.all_time,
        thisMonth: raw.manager_earnings.this_month,
        // From the DB, not Grow: lifetime manager revenue share rather than a
        // settled balance — there is no payout ledger yet. The container
        // disables Withdraw unless this is > 0.
        currentBalance: raw.manager_earnings.current_balance,
        monthly: sparkPoints(raw.manager_earnings.monthly),
      },

      community: {
        members: raw.community.members,
        membersThisWeek: raw.community.members_this_week,
        suppliers: raw.community.suppliers,
        suppliersThisWeek: raw.community.suppliers_this_week,
        memberAvatars: raw.community.member_avatars,
        supplierAvatars: raw.community.supplier_avatars,
      },

      quality: {
        services: quality(raw.quality.services),
        products: quality(raw.quality.products),
        // Older API builds don't send this block; the tab stays "soon" then.
        restaurants: raw.quality.restaurants ? quality(raw.quality.restaurants) : null,
      },

      topProductCategories: (raw.top_product_categories || []).map((c) => ({
        name: productCategoryName(c.name),
        image: c.image,
        count: c.count,
        revenue: c.revenue,
      })),
      topRestaurantCategories: (raw.top_restaurant_categories || []).map((c) => ({
        name: restaurantCategoryName(c.name),
        image: c.image,
        count: c.count,
        revenue: c.revenue,
      })),
      topServicePricelists: (raw.top_service_pricelists || []).map((c) => ({
        name: serviceCategoryName(c.name),
        image: c.image,
        count: c.count,
        revenue: c.revenue,
      })),
      topSuppliers: (raw.top_suppliers || []).map((s) => ({
        name: s.name,
        image: s.image,
        revenue: s.revenue,
      })),
    };
  }, [raw, lang, tCategories, tProductCategories, tRestaurantCategories]);

  return { data, isLoading, error, refetch: fetchDashboard };
};

export default useCommunityDashboard;
